// Ducks in a Row: the counting and wording for the Sidekick's read-only view,
// free of React. Mirrors sidekick-client's lib/ducks.ts, worded for someone
// looking at a client's list rather than their own.
import type { Duck, DuckArea, DuckCadence } from "@shared/ducks";

export const DUCK_CADENCE_LABEL: Record<DuckCadence, string> = {
  once: "Once-off",
  weekly: "Weekly",
  monthly: "Monthly",
  quarterly: "Every 3 months",
  half: "Every 6 months",
  yearly: "Yearly",
  y2: "Every 2 years",
  y5: "Every 5 years",
  y10: "Every 10 years",
  due: "When due",
};

export type DuckSummary = { total: number; sorted: number; onIt: number; pct: number };

/** Ducks that count: in a live area and not deleted as not applicable. */
export function liveDucks(ducks: Duck[], areas: DuckArea[]): Duck[] {
  const gone = new Set(areas.filter((a) => a.deleted).map((a) => a.key));
  return ducks.filter((d) => d.status !== "na" && !gone.has(d.area_key));
}

/** Areas worth showing: not deleted, and with at least one live duck. */
export function shownAreas(areas: DuckArea[], live: Duck[]): DuckArea[] {
  const used = new Set(live.map((d) => d.area_key));
  return areas.filter((a) => !a.deleted && used.has(a.key));
}

/** What the client took out of their list, for the "Doesn't apply" footer:
 * whole areas, then single ducks from areas that are still there. */
export function notApplicable(ducks: Duck[], areas: DuckArea[]): { areas: DuckArea[]; ducks: Duck[] } {
  const gone = new Set(areas.filter((a) => a.deleted).map((a) => a.key));
  const order = areas.map((a) => a.key);
  return {
    areas: areas.filter((a) => a.deleted),
    // Grouped by area in page order; the API's order is catalog position,
    // which interleaves areas.
    ducks: ducks
      .filter((d) => d.status === "na" && !gone.has(d.area_key))
      .sort((a, b) => order.indexOf(a.area_key) - order.indexOf(b.area_key)),
  };
}

export function summarise(ducks: Duck[]): DuckSummary {
  const total = ducks.length;
  const sorted = ducks.filter((d) => d.status === "done").length;
  const onIt = ducks.filter((d) => d.status === "on").length;
  return { total, sorted, onIt, pct: total ? Math.round((sorted / total) * 100) : 0 };
}

/** Both the overview and a client's own list belong to the same nav item. */
export function isDucksRoute(location: string): boolean {
  return location === "/ducks" || location.startsWith("/clients/ducks/");
}

/** How many of a row of `marks` ducks to fill. Never rounds a started list down
 * to an empty row, or an unfinished one up to a full row. */
export function filledMarks(sorted: number, total: number, marks: number): number {
  if (total <= 0 || sorted <= 0) return 0;
  if (sorted >= total) return marks;
  return Math.min(marks - 1, Math.max(1, Math.round((sorted / total) * marks)));
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "6 Oct", or "6 Oct 2027" when it isn't this year. Spelt out by hand: the
 * locale formats disagree on "06 Oct" and "Sept" between browsers. */
export function dayLabel(iso: string, now = new Date()): string {
  const [y, m, day] = iso.slice(0, 10).split("-").map(Number);
  const label = `${day} ${MONTHS[m - 1]}`;
  return y === now.getFullYear() ? label : `${label} ${y}`;
}

/** "For people with a pool" from the catalog's "People with a pool". */
export function audienceLabel(audience: string): string {
  if (!audience) return "";
  return `For ${audience.charAt(0).toLowerCase()}${audience.slice(1)}`;
}

export type DuckState = {
  /** The chip under the title. */
  text: string;
  tone: "green" | "blue" | "amber" | "grey";
};

/** Where a duck stands, in a few words. `me` is the viewing Sidekick, whose
 * own tasks read "you". */
export function duckState(d: Duck, me?: string | null, now = new Date()): DuckState {
  if (d.due_again) {
    return { text: d.next_due ? `Due again since ${dayLabel(d.next_due, now)}` : "Due again", tone: "amber" };
  }
  if (d.status === "on") {
    const who = d.task?.assignee;
    const parts = ["In their tracker"];
    if (who) parts.push(who.uid === me ? "with you" : `with ${who.name ?? (who.kind === "client" ? "the client" : "a Sidekick")}`);
    if (d.task?.due_date) parts.push(`due ${dayLabel(d.task.due_date, now)}`);
    return { text: parts.join(", "), tone: "blue" };
  }
  if (d.status === "done") {
    const parts = [d.sorted_on ? `Sorted ${dayLabel(d.sorted_on, now)}` : "Sorted"];
    if (d.next_due) parts.push(`next due ${dayLabel(d.next_due, now)}`);
    return { text: parts.join(", "), tone: "green" };
  }
  return { text: "Not started", tone: "grey" };
}
